import { stakeholderApi, stakeholderEndpoints } from '@/config/stakeholder-api';

// Types
export interface SecurityGap {
  id: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  nistCategory: string;
  nistSubcategory: string;
  personaName: string;
  personaId: string;
  points: number;
  stakeholderSources: string[]; // All stakeholders who can reveal this gap
  createdAt: string;
}

export interface GapStakeholder {
  id: string;
  name: string;
}

export interface GapFilters {
  nistFunction?: 'identify' | 'protect' | 'detect' | 'respond' | 'recover';
  severity?: 'critical' | 'high' | 'medium' | 'low';
  stakeholderId?: string;
  revealed?: boolean;
  limit?: number;
  offset?: number;
}

export interface GapListResponse {
  gaps: SecurityGap[];
  total: number;
  limit: number;
  offset: number;
  nistSummary: Record<string, {
    count: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
  }>;
}

/**
 * Gap Service - All gap-related API calls
 * Backend is single source of truth for gap data from PostgreSQL
 */
export const gapService = {
  /**
   * Get all gaps with optional filters
   */
  getAllGaps: async (filters?: GapFilters): Promise<GapListResponse> => {
    const params = new URLSearchParams();

    if (filters?.nistFunction) params.append('nistFunction', filters.nistFunction);
    if (filters?.severity) params.append('severity', filters.severity);
    if (filters?.stakeholderId) params.append('stakeholderId', filters.stakeholderId);
    if (filters?.revealed !== undefined) params.append('revealed', String(filters.revealed));
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));

    const queryString = params.toString();
    const url = `/gaps${queryString ? `?${queryString}` : ''}`;

    const response = await stakeholderApi.get(url);
    return response.data?.data || { gaps: [], total: 0, limit: 50, offset: 0, nistSummary: {} };
  },

  /**
   * Get a specific gap by ID
   */
  getGap: async (gapId: string): Promise<SecurityGap | null> => {
    try {
      const response = await stakeholderApi.get(`/gaps/${gapId}`);
      return response.data?.data || null;
    } catch (error) {
      console.error(`Failed to fetch gap ${gapId}:`, error);
      return null;
    }
  },

  /**
   * Get all gaps discoverable by a specific stakeholder
   * Uses consolidated endpoint: GET /api/v2/stakeholders/:id/gaps
   */
  getStakeholderGaps: async (stakeholderId: string): Promise<SecurityGap[]> => {
    try {
      const response = await stakeholderApi.get(`/stakeholders/${stakeholderId}/gaps`);
      return response.data?.data?.gaps || [];
    } catch (error) {
      console.error(`Failed to fetch gaps for stakeholder ${stakeholderId}:`, error);
      return [];
    }
  },

  /**
   * Get all stakeholders who can reveal a specific gap
   * Used for quiz retry - when student fails quiz 3 times, they can try with different stakeholder
   * Uses new endpoint: GET /api/v2/gaps/:id/stakeholders
   */
  getGapStakeholders: async (gapId: string): Promise<GapStakeholder[]> => {
    try {
      const response = await stakeholderApi.get(`/gaps/${gapId}/stakeholders`);
      return response.data?.data?.stakeholders || [];
    } catch (error) {
      console.error(`Failed to fetch stakeholders for gap ${gapId}:`, error);
      return [];
    }
  },

  /**
   * Get gap discovery configuration for all gaps
   * Returns mapping of gaps to stakeholders for frontend discovery logic
   */
  getGapDiscoveryConfig: async (): Promise<Record<string, {
    gapId: string;
    stakeholders: string[];
    trustThreshold: number;
    keywords: string[];
  }>> => {
    try {
      // Fetch all gaps
      const response = await stakeholderApi.get('/gaps?limit=100');
      const gaps = response.data?.data?.gaps || [];

      // Build discovery config from gap data
      const config: Record<string, any> = {};

      gaps.forEach((gap: SecurityGap) => {
        if (!config[gap.id]) {
          config[gap.id] = {
            gapId: gap.id,
            stakeholders: gap.stakeholderSources || [],
            trustThreshold: 0.6, // Default trust threshold
            keywords: extractKeywords(gap.title, gap.description)
          };
        }
      });

      return config;
    } catch (error) {
      console.error('Failed to fetch gap discovery config:', error);
      return {};
    }
  }
};

/**
 * Helper function to extract keywords from gap title and description
 * Used for gap discovery triggers during conversations
 */
function extractKeywords(title: string, description: string): string[] {
  const text = `${title} ${description}`.toLowerCase();
  const keywords: string[] = [];

  // Common security keywords
  if (text.includes('password')) keywords.push('password', 'passwords');
  if (text.includes('access')) keywords.push('access', 'authentication');
  if (text.includes('encryption')) keywords.push('encryption', 'encrypted');
  if (text.includes('backup')) keywords.push('backup', 'backups');
  if (text.includes('patch')) keywords.push('patch', 'patching', 'updates');
  if (text.includes('log')) keywords.push('log', 'logging', 'logs');
  if (text.includes('audit')) keywords.push('audit', 'auditing');
  if (text.includes('monitoring')) keywords.push('monitoring', 'monitor');
  if (text.includes('incident')) keywords.push('incident', 'incidents');
  if (text.includes('recovery')) keywords.push('recovery', 'disaster');
  if (text.includes('training')) keywords.push('training', 'awareness');
  if (text.includes('firewall')) keywords.push('firewall', 'network');

  return [...new Set(keywords)]; // Remove duplicates
}